import { useState } from 'react';
import { GitBranch } from 'lucide-react';
import { useProjectStore } from '../../stores/useProjectStore';
import styles from './Modal.module.css';

interface CloneGitModalProps {
  onClose: () => void;
  onCreated: (projectId: string) => Promise<void>;
}

export function CloneGitModal({ onClose, onCreated }: CloneGitModalProps) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const createProject = useProjectStore(s => s.createProject);

  const handleClone = async () => {
    const repoUrl = url.trim();
    if (!repoUrl) {
      setError('Repository URL is required.');
      return;
    }

    const projectName = name.trim() || deriveNameFromUrl(repoUrl);
    if (!projectName) {
      setError('Could not determine project name. Please enter one manually.');
      return;
    }

    setIsCloning(true);
    setError(null);

    try {
      // Browser-based git clone is not natively supported.
      // Create a placeholder project with a README indicating the clone source.
      const readmeContent = `# ${projectName}\n\nCloned from: ${repoUrl}\n\n> **Note:** Full git clone is not available in the browser IDE.\n> Use the CLI to clone this repository, then import the files.\n`;
      const id = await createProject(projectName, 'git-clone', [
        {
          id: crypto.randomUUID(),
          name: 'README.md',
          path: 'README.md',
          content: readmeContent,
          language: 'markdown',
          isModified: false,
        },
      ]);
      await onCreated(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create project.');
      setIsCloning(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Clone from Git</h2>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div className={styles.modalBody}>
          {error && <div className={styles.errorBanner}>{error}</div>}

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Repository URL</label>
            <div style={{ position: 'relative' }}>
              <GitBranch
                size={16}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-secondary)',
                  pointerEvents: 'none',
                }}
              />
              <input
                className={styles.fieldInput}
                style={{ paddingLeft: '32px' }}
                value={url}
                onChange={e => {
                  setUrl(e.target.value);
                  if (!name) {
                    const derived = deriveNameFromUrl(e.target.value);
                    if (derived) setName(derived);
                  }
                }}
                placeholder="https://github.com/user/repo.git"
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Project Name</label>
            <input
              className={styles.fieldInput}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="my-project"
            />
          </div>

          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 0' }}>
            Git clone runs in a sandboxed environment. Large repositories may take longer to process.
          </p>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button className={styles.submitBtn} onClick={handleClone} disabled={isCloning || !url.trim()}>
            {isCloning ? 'Cloning...' : 'Clone Repository'}
          </button>
        </div>
      </div>
    </div>
  );
}

function deriveNameFromUrl(url: string): string {
  try {
    const cleaned = url.trim().replace(/\.git$/, '').replace(/\/$/, '');
    const lastSegment = cleaned.split('/').pop();
    return lastSegment ?? '';
  } catch {
    return '';
  }
}
